import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Plus, Edit, Trash2, Info, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Templates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    template_type: 'charge_reminder',
    days_before: 0,
    active: true,
    group_id: ''
  });

  const selectedGroupId = searchParams.get('group');

  useEffect(() => {
    fetchTemplates();
    fetchGroups();
  }, []);

  // Filter templates when group changes or templates load
  useEffect(() => {
    if (selectedGroupId) {
      setFilteredTemplates(templates.filter(t => t.group_id === selectedGroupId));
    } else {
      setFilteredTemplates(templates);
    }
  }, [selectedGroupId, templates]);

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos');
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template atualizado!');
      } else {
        await axios.post(`${API}/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template criado!');
      }
      fetchTemplates();
      setOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template excluído!');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao excluir template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      content: '',
      template_type: 'charge_reminder',
      days_before: 0,
      active: true,
      group_id: ''
    });
    setEditingTemplate(null);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      template_type: template.template_type,
      days_before: template.days_before || 0,
      active: template.active,
      group_id: template.group_id || ''
    });
    setOpen(true);
  };

  const templateTypeLabels = {
    charge_reminder: 'Lembrete de Cobrança',
    renewal: 'Renovação',
    trial_expiring: 'Expiração de Teste',
    custom: 'Personalizado'
  };

  const daysBeforeLabels = {
    '-2': '2 dias após vencimento',
    '-1': '1 dia após vencimento',
    '0': 'No dia do vencimento',
    '1': '1 dia antes do vencimento',
    '2': '2 dias antes do vencimento'
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="templates-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">
                Templates de Mensagens
              </h1>
              {selectedGroup && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/20 border border-violet-500/30">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: selectedGroup.color || '#6366f1' }}
                  />
                  <span className="text-sm text-violet-400 font-medium">{selectedGroup.name}</span>
                  <button
                    onClick={() => setSearchParams({})}
                    className="p-0.5 hover:bg-violet-500/30 rounded-full transition-colors"
                    title="Remover filtro"
                  >
                    <X className="w-3.5 h-3.5 text-violet-400" />
                  </button>
                </div>
              )}
            </div>
            <p className="text-slate-400">
              {selectedGroup 
                ? `Mostrando templates do grupo "${selectedGroup.name}"`
                : 'Configure mensagens automáticas para seus clientes'
              }
            </p>
          </div>
          
          <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="add-template-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Template
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingTemplate ? 'Editar Template' : 'Novo Template'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="template-form">
                <div>
                  <Label htmlFor="name">Nome do Template</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Ex: Lembrete 2 dias antes"
                    data-testid="template-name-input"
                  />
                </div>

                <div>
                  <Label>Tipo de Template</Label>
                  <Select value={formData.template_type} onValueChange={(value) => setFormData({...formData, template_type: value})}>
                    <SelectTrigger data-testid="template-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="charge_reminder">Lembrete de Cobrança</SelectItem>
                      <SelectItem value="renewal">Renovação</SelectItem>
                      <SelectItem value="trial_expiring">🧪 Expiração de Teste</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Grupo *</Label>
                  <Select value={formData.group_id || 'none'} onValueChange={(value) => setFormData({...formData, group_id: value === 'none' ? null : value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.length === 0 ? (
                        <SelectItem value="none" disabled>Nenhum grupo criado</SelectItem>
                      ) : (
                        groups.map(group => (
                          <SelectItem key={group.id} value={group.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{backgroundColor: group.color}}></div>
                              {group.name}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Este template será enviado apenas para clientes do grupo selecionado</p>
                </div>

                {formData.template_type === 'charge_reminder' && (
                  <div>
                    <Label>Dias antes/depois do vencimento</Label>
                    <Select value={formData.days_before?.toString()} onValueChange={(value) => setFormData({...formData, days_before: parseInt(value)})}>
                      <SelectTrigger data-testid="template-days-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 dias antes</SelectItem>
                        <SelectItem value="1">1 dia antes</SelectItem>
                        <SelectItem value="0">No dia do vencimento</SelectItem>
                        <SelectItem value="-1">1 dia após vencimento</SelectItem>
                        <SelectItem value="-2">2 dias após vencimento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.template_type === 'trial_expiring' && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-400 flex items-center">
                      <Info className="w-4 h-4 mr-2" />
                      Este template será enviado 15 minutos antes do teste expirar.
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Variáveis extras: <code className="text-amber-400">{'{tempo_restante}'}</code> (minutos) e <code className="text-amber-400">{'{horas_teste}'}</code> (duração total)
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="content">Mensagem</Label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    className="w-full h-32 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    placeholder="Olá {name}, sua cobrança de R$ {amount} vence em {date}."
                    required
                    data-testid="template-content-input"
                  />
                  <div className="mt-2 p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg">
                    <p className="text-xs text-sky-400 mb-2 flex items-center">
                      <Info className="w-4 h-4 mr-2" />
                      Variáveis disponíveis:
                    </p>
                    <div className="text-xs text-slate-300 space-y-1 grid grid-cols-2 gap-x-4">
                      <p><span className="mono text-sky-400">{'{name}'}</span> - Nome do cliente</p>
                      <p><span className="mono text-sky-400">{'{phone}'}</span> - Telefone</p>
                      <p><span className="mono text-sky-400">{'{amount}'} ou {'{valor}'}</span> - Valor</p>
                      <p><span className="mono text-sky-400">{'{date}'} ou {'{data}'}</span> - Data vencimento</p>
                      <p><span className="mono text-sky-400">{'{status}'}</span> - Status</p>
                      <p><span className="mono text-sky-400">{'{produto}'} ou {'{plano}'}</span> - Produto/Plano</p>
                      <p><span className="mono text-sky-400">{'{notes}'} ou {'{informacoes}'}</span> - Observações</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="active">Template ativo</Label>
                </div>

                <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="template-submit-btn">
                  {editingTemplate ? 'Atualizar' : 'Criar'} Template
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Carregando...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">
              {selectedGroup 
                ? `Nenhum template cadastrado para o grupo "${selectedGroup.name}".`
                : 'Nenhum template cadastrado. Crie seu primeiro template!'
              }
            </p>
            {selectedGroup && (
              <button
                onClick={() => setSearchParams({})}
                className="mt-4 text-sm text-violet-400 hover:text-violet-300 underline"
              >
                Ver todos os templates
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTemplates.map((template) => {
              const templateGroup = groups.find(g => g.id === template.group_id);
              return (
              <div key={template.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid={`template-${template.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white">{template.name}</h3>
                      {template.active ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-600">
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                          Inativo
                        </span>
                      )}
                      {templateGroup && !selectedGroup && (
                        <span 
                          className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                          style={{ 
                            backgroundColor: `${templateGroup.color}20`,
                            color: templateGroup.color,
                            borderColor: `${templateGroup.color}50`,
                            borderWidth: '1px'
                          }}
                        >
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: templateGroup.color }}
                          />
                          {templateGroup.name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mb-2">{templateTypeLabels[template.template_type]}</p>
                    {template.days_before !== null && template.template_type === 'charge_reminder' && (
                      <p className="text-xs text-slate-500">{daysBeforeLabels[template.days_before]}</p>
                    )}
                  </div>
                  <MessageSquare className="w-8 h-8 text-violet-500" />
                </div>
                
                <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{template.content}</p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleEdit(template)}
                    data-testid={`edit-template-${template.id}`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    className="text-rose-600 border-rose-600 hover:bg-rose-600 hover:text-white"
                    onClick={() => handleDelete(template.id)}
                    data-testid={`delete-template-${template.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
